/**
 * Frozen two-zone-v2 payment-page stylesheet template (Spec 340).
 *
 * PROVENANCE: derived from the real Facade two-zone-v2 CSS structure (the `.tz-*` /
 * `.facade-*` selectors, the `.tz-cta-btn` block incl. its 3-part glow box-shadow,
 * and the `.facade-payment-page` background block), captured from a live Dev service
 * stylesheet on 2026-06-19. We keep the non-parametrised rules verbatim and only
 * interpolate the CTA fill / glow / radius and the background-image — the only
 * per-merchant delta the brainstorm identified. Frozen in-repo so generation is
 * reproducible for builders and CI (no live fetch).
 *
 * COUPLING (accepted, per spec): we own this template. If the Facade changes the
 * two-zone-v2 render structure, we resync this file. The exact rule set should be
 * diffed against the canonical prod two-zone-v2 stylesheet (Replika
 * `SVC_1777291778_b2d77d0c`) during the deferred Phase-4 Dev/prod smoke and
 * resynced if it differs.
 *
 * Pure module — no DOM, no network. The CTA/bg fragments are computed by
 * `generate-stylesheet.ts` and passed in here; this module only assembles them into
 * the fixed rule set.
 */

/** Pre-computed CSS fragments the generator interpolates into the fixed template. */
export interface TwoZoneV2TemplateParts {
  /**
   * Full `background-image: url("…") !important;` declaration, or '' to omit it
   * (e.g. when the service has no background image — the background-color fallback
   * remains).
   */
  backgroundImageDecl: string;
  /**
   * CTA fill declaration(s): a solid `background`/`background-color` pair, or a
   * `linear-gradient(...)` background plus a `background-color` fallback. Each line
   * already carries `!important`.
   */
  ctaBackgroundDecl: string;
  /** CTA `border-radius` value in px. */
  ctaBorderRadiusPx: number;
  /** CTA `box-shadow` value (without the `box-shadow:` prefix or `!important`). */
  ctaBoxShadow: string;
}

/** Indent a multi-line fragment to line up inside a rule body. */
function indent(fragment: string, spaces = 2): string {
  const pad = ' '.repeat(spaces);
  return fragment
    .split('\n')
    .map((line) => (line.trim() === '' ? '' : pad + line.trim()))
    .join('\n');
}

/**
 * Assemble the full two-zone-v2 stylesheet body (without the `@theme-knobs` header —
 * the generator prepends that) from the fixed rules + the computed CTA/bg fragments.
 */
export function buildTwoZoneV2Css(parts: TwoZoneV2TemplateParts): string {
  const bgImageLine = parts.backgroundImageDecl ? `\n${indent(parts.backgroundImageDecl)}` : '';
  const ctaBackground = indent(parts.ctaBackgroundDecl);

  return `body.facade-payment-page, .facade-payment-page {${bgImageLine}
  background-size: 100% auto !important;
  background-position: top center !important;
  background-repeat: no-repeat !important;
  background-color: #1a1a2e !important;
  background-attachment: scroll !important;
  min-height: 100vh !important;
  margin: 0 !important;
  box-sizing: border-box !important;
}

.facade-card {
  width: 88% !important;
  max-width: 440px !important;
  margin: 0 auto !important;
  background: transparent !important;
  text-align: center !important;
  font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
}

.facade-card h1 {
  color: #fff !important;
  font-size: 27px !important;
  font-weight: 700 !important;
  text-align: center !important;
  margin: 0 0 10px !important;
  line-height: 1.15 !important;
  text-shadow: 0 2px 8px rgba(0,0,0,0.5) !important;
}

.facade-card > p {
  color: rgba(255,255,255,0.78) !important;
  font-size: 15px !important;
  line-height: 1.4 !important;
  text-align: center !important;
  margin: 0 auto 18px !important;
  max-width: 92% !important;
  text-shadow: 0 1px 4px rgba(0,0,0,0.5) !important;
}

/* Zone 1 — price */
.facade-compliance-zone .tz-price-text,
.tz-price-text {
  color: #fff !important;
  font-size: 27px !important;
  font-weight: 700 !important;
  text-shadow: 0 2px 8px rgba(0,0,0,0.5) !important;
}

/* CTA button — fill / glow / radius are the editor's knobs */
.facade-compliance-zone .tz-cta-btn,
.tz-cta-btn {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 88% !important;
  max-width: 360px !important;
  height: auto !important;
  min-height: 68px !important;
  margin: 18px auto !important;
  padding: 18px 16px !important;
${ctaBackground}
  color: #fff !important;
  font-size: 25px !important;
  font-weight: 700 !important;
  border: 1px solid rgba(180,235,255,0.45) !important;
  border-radius: ${parts.ctaBorderRadiusPx}px !important;
  box-shadow: ${parts.ctaBoxShadow} !important;
  letter-spacing: 0.3px !important;
  cursor: pointer !important;
  text-transform: none !important;
}

/* Zone 2 — legal */
.facade-compliance-zone .tz-legal,
.tz-legal {
  color: rgba(255,255,255,0.55) !important;
  font-size: 12px !important;
  line-height: 1.5 !important;
  text-align: center !important;
  padding: 6px 6% 24px !important;
  text-shadow: 0 1px 3px rgba(0,0,0,0.5) !important;
}
.tz-legal strong { color: rgba(255,255,255,0.8) !important; font-weight: 600 !important; }

/* Zone 2 — footer links (2-row grid) */
.facade-compliance-zone .tz-footer-links,
.tz-footer-links {
  display: grid !important;
  grid-template-columns: 1fr 1fr 1fr !important;
  grid-template-areas:
    "agb        .       widerruf"
    "impressum  hilfe   datenschutz" !important;
  gap: 10px 0 !important;
  width: 90% !important;
  max-width: 380px !important;
  margin: 10px auto 26px !important;
  padding: 0 !important;
  align-items: center !important;
  position: static !important;
}
.facade-compliance-zone .tz-footer-links a,
.tz-footer-links a {
  color: rgba(255,255,255,0.72) !important;
  font-size: 14px !important;
  text-decoration: underline !important;
}
.tz-footer-links a:nth-child(1) { grid-area: agb !important; text-align: left !important; }
.tz-footer-links a:nth-child(2) { grid-area: impressum !important; text-align: left !important; }
.tz-footer-links a:nth-child(3) { grid-area: datenschutz !important; text-align: right !important; }
.tz-footer-links a:nth-child(4) { grid-area: widerruf !important; text-align: right !important; }
.tz-footer-links a:nth-child(5) { grid-area: hilfe !important; text-align: center !important; }
`;
}
